import { createContext, useContext, type ReactNode } from 'react';
import { t as translate, STRINGS, type I18nKey, type Language } from '@harmony/shared';
import { ApiError } from './api';

const LangContext = createContext<Language>('en');

export function LanguageProvider({ lang, children }: { lang: Language; children: ReactNode }) {
  return <LangContext.Provider value={lang}>{children}</LangContext.Provider>;
}

export function useLang(): Language {
  return useContext(LangContext);
}

/** Returns a translator bound to the user's current language. */
export function useT() {
  const lang = useContext(LangContext);
  return (key: I18nKey, vars?: Record<string, string | number>) => translate(lang, key, vars);
}

/** Turns an API error code into a friendly, translated sentence. */
export function useErrorText() {
  const t = useT();
  return (e: unknown): string => {
    const code = e instanceof ApiError ? e.issues[0]?.message ?? e.code : typeof e === 'string' ? e : '';
    const key = `err.${code}` as I18nKey;
    return key in STRINGS.en ? t(key) : t('error.generic');
  };
}

/** Picks the English or Amharic name from a record with nameEn/nameAm. */
export function useName() {
  const lang = useContext(LangContext);
  return (x: { nameEn: string; nameAm: string } | null | undefined) => (x ? (lang === 'am' ? x.nameAm : x.nameEn) : '');
}
